import { z } from 'zod'

/**
 * Definicao de carta e da DSL de efeitos.
 * Fonte da verdade: docs/02-catalogo-de-cartas.md
 *
 * IMPORTANTE: este arquivo descreve a FORMA de uma carta, nunca uma carta concreta.
 * Nenhum nome de carta, personagem ou marca pertence a este pacote. Ver docs/99-aviso-legal.md
 */

// ---------------------------------------------------------------------------
// Enumeracoes de dominio
// ---------------------------------------------------------------------------

/** Terrenos das zonas de monstro. docs/01-regras-do-duelo.md secao 3.2 */
export const TERRAINS = ['GRASSLAND', 'FOREST', 'MOUNTAIN', 'WASTELAND', 'SEA', 'DARKNESS', 'NEUTRAL'] as const
export type Terrain = (typeof TERRAINS)[number]

/**
 * Modo de terreno do duelo. docs/01-regras-do-duelo.md secao 3
 *
 * `MIXED`   — o modo que a regra e a tela chamam de **ARENA**: mapa sorteado no inicio e fixo pelo
 *             duelo. E o modo de sempre, e o padrao de todo duelo que nao escolhe outro
 *             (docs/09 secao 1). O identificador nao acompanhou o nome de proposito — renomea-lo
 *             custaria migracao e reescreveria a coluna de todo duelo ja jogado. Esta e a UNICA
 *             nota que liga os dois; repeti-la em cada documento seria um lugar a mais para
 *             desatualizar.
 * `CLASSIC` — as 5 zonas nascem NEUTRAL e nada as sorteia; so a Magia de Campo pinta o campo. E o
 *             unico dos tres que produz duelo RANQUEADO (docs/09 secao 1).
 * `FUN`     — mapa sorteado no inicio e RE-SORTEADO a cada rodada (docs/01 secao 3).
 *
 * A ordem desta tupla e CANONICA, e nao estetica: o sorteio do modo na interseccao de dois
 * jogadores (docs/09 secao 2.0) ordena por ela antes de sortear. Sem uma ordem fixa, a mesma
 * interseccao vinda em ordens diferentes daria modos diferentes na mesma semente, e o sorteio
 * deixaria de ser funcao da semente.
 */
export const TERRAIN_MODES = ['MIXED', 'CLASSIC', 'FUN'] as const
export type TerrainMode = (typeof TERRAIN_MODES)[number]

/**
 * O modo de **quem nao escolhe**. docs/01 secao 3, docs/09 secao 1.
 *
 * Quem escolhe sao dois, e nao um: a fila escolhe por duelo, com o jogador, na hora; a trilha
 * premiada escolhe por trilha, no cadastro dela — e o padrao DELA e outro, `CLASSIC`, declarado em
 * `@rdd/content` com o motivo (docs/06 secao 17.1). Sem esta nota, quem le esta linha conclui que
 * todo duelo sem escolha explicita e arena, e a trilha deixou de ser um deles.
 */
export const DEFAULT_TERRAIN_MODE: TerrainMode = 'MIXED'

export const MONSTER_TYPES = [
  'DRAGON',
  'SPELLCASTER',
  'ZOMBIE',
  'WARRIOR',
  'BEAST_WARRIOR',
  'BEAST',
  'WINGED_BEAST',
  'FIEND',
  'FAIRY',
  'INSECT',
  'DINOSAUR',
  'REPTILE',
  'FISH',
  'SEA_SERPENT',
  'MACHINE',
  'THUNDER',
  'AQUA',
  'PYRO',
  'PLANT',
  'ROCK',
] as const
export type MonsterType = (typeof MONSTER_TYPES)[number]

export const ATTRIBUTES = ['LIGHT', 'DARK', 'EARTH', 'WATER', 'FIRE', 'WIND', 'DIVINE'] as const
export type Attribute = (typeof ATTRIBUTES)[number]

export const RARITIES = ['COMMON', 'RARE', 'SUPER', 'ULTRA', 'SECRET'] as const
export type Rarity = (typeof RARITIES)[number]

/**
 * O tipo de impressao de uma carta. docs/02 secao 1, ADR 0045.
 *
 * Short Print e Super Short Print sao impressoes de Comum: a raridade fica `COMMON`, e a escassez
 * vem aqui. E so informativo — nao entra no sorteio, no preco nem em regra de deck.
 */
export const PRINT_TYPES = ['NORMAL', 'SHORT_PRINT', 'SUPER_SHORT_PRINT'] as const
export type PrintType = (typeof PRINT_TYPES)[number]

/** O tipo de impressao de uma carta, com a ausencia lida como `NORMAL` (ADR 0045). */
export const printTypeOf = (card: { printType?: PrintType | undefined }): PrintType => card.printType ?? 'NORMAL'

/**
 * A raridade e o tipo de impressao CORRENTES de cada carta, como o `api` os serve em
 * `GET /cards/curation` (ADR 0045). O cliente os sobrepoe ao catalogo do bundle, que so sabe os de
 * estreia.
 */
export const cardCurationSchema = z.record(
  z.string(),
  z.object({ rarity: z.enum(RARITIES), printType: z.enum(PRINT_TYPES) }),
)
export type CardCuration = z.infer<typeof cardCurationSchema>

export const CARD_KINDS = ['MONSTER', 'SPELL', 'TRAP'] as const
export type CardKind = (typeof CARD_KINDS)[number]

/**
 * `FUSION` e a "Carta de Fusao" exigida por docs/01-regras-do-duelo.md secao 7. `RITUAL` e a Magia
 * de Ritual (docs/01 secao 4.3): ela nomeia o monstro que invoca, em `rituals.json`.
 * `CONTINUOUS` permanece na zona de magia e e CONSULTADA enquanto estiver la (docs/01 secao 6.0).
 */
export const SPELL_KINDS = ['NORMAL', 'EQUIP', 'FIELD', 'QUICK', 'FUSION', 'CONTINUOUS', 'RITUAL'] as const
export type SpellKind = (typeof SPELL_KINDS)[number]

/**
 * Subtipo da Armadilha. docs/01-regras-do-duelo.md secao 6.0
 *
 * `CONTINUOUS` permanece em campo depois de ativada, como a Magia Continua.
 * `COUNTER` anula o efeito de outra carta, em resposta a ela (docs/01 secao 6.5).
 *
 * O padrao e `NORMAL`, para que as armadilhas ja existentes continuem validas sem edicao.
 */
export const TRAP_KINDS = ['NORMAL', 'CONTINUOUS', 'COUNTER'] as const
export type TrapKind = (typeof TRAP_KINDS)[number]

// ---------------------------------------------------------------------------
// DSL de efeitos. docs/02-catalogo-de-cartas.md secao 3
// ---------------------------------------------------------------------------

export const EFFECT_TRIGGERS = [
  'ON_ACTIVATE',
  'ON_ATTACK_DECLARED',
  /**
   * A janela antes do calculo de dano: a da declaracao fechou sem anular o ataque, e o defensor virado ja foi revelado.
   * E o texto "durante o calculo de dano". docs/01-regras-do-duelo.md secao 6.4, docs/backlog/160
   *
   * Abre em todo ataque, direto inclusive, para o oponente do atacante, e so com carta elegivel: o efeito com este
   * gatilho (Kuriboh da mao, Sanga face-up quando e o alvo), a segunda porta que so muda ATK ou DEF (Muralhas do
   * Castelo) e a Armadilha Contra, que responde ao elo.
   */
  'ON_DAMAGE_CALCULATION',
  /**
   * NAO sao gatilho de carta: sao o nome das duas janelas que abrem no turno do oponente sem que ele faca nada — no
   * inicio da Batalha e na Final que encerra o turno. Entram a Armadilha da segunda porta e a Continua; o carregamento
   * recusa carta que os declare. docs/01-regras-do-duelo.md secao 6.4, docs/backlog/161
   */
  'ON_BATTLE_PHASE_START',
  'ON_END_PHASE',
  'ON_SUMMON',
  'ON_FLIP',
  'ON_DESTROYED_BY_BATTLE',
  /**
   * O proprio monstro foi ATACADO numa batalha declarada, depois do dano ser aplicado.
   * docs/01-regras-do-duelo.md secao 5.2 e 6.6
   *
   * Gatilho INVOLUNTARIO do DEFENSOR — nao abre janela de resposta, e nunca dispara para ataque
   * direto (que nao tem defensor). E o momento DEPOIS de `ON_ATTACK_DECLARED` (que e antes do
   * calculo): os dois sao pontos diferentes da mesma batalha. Dispara mesmo que o defensor tenha
   * sido destruido nesta mesma batalha, mesmo raciocinio de `ON_DESTROYED_BY_BATTLE` acima. Nao
   * dispara se o duelo ja terminou por essa batalha (leva_sdk_sdy/curadoria, Wall of Illusion).
   */
  'ON_ATTACKED_AFTER_DAMAGE',
  'ON_TURN_START',
  'ON_TURN_END',
  /**
   * Efeito CONSULTADO de uma carta Continua, enquanto ela estiver face-up em campo.
   * docs/01-regras-do-duelo.md secao 6.0
   *
   * Nao dispara: e lido a cada calculo de valor efetivo. Tem gatilho proprio, e nao reusa
   * `ON_ACTIVATE`, porque uma Continua pode ter as DUAS coisas — um efeito que resolve uma vez
   * ao ser ativada, e outro que vale enquanto ela estiver la.
   */
  'WHILE_FACE_UP',
  /**
   * Outra carta acabou de ser ativada. docs/01-regras-do-duelo.md secao 6.5
   *
   * E o gatilho da Armadilha Contra, e o unico que responde ao ELO ANTERIOR da corrente em vez de
   * responder ao gatilho que a abriu.
   */
  'ON_CARD_ACTIVATED',
  /**
   * O jogador declarou o uso da habilidade especial de um MONSTRO.
   * docs/01-regras-do-duelo.md secao 6.1
   *
   * Nao reusa `ON_ACTIVATE`, que e da Magia: as condicoes de legalidade sao outras — o monstro
   * precisa estar em campo, face-up, e so ativa uma vez por turno. Com o custo de descartar a si mesmo, a habilidade e
   * da MAO, e so da mao (Thunder Dragon, `leva_mrd/curadoria`).
   */
  'ON_MONSTER_EFFECT',
  /**
   * Invocacao-Normal (face-up) ou Invocacao por Virada, de QUALQUER jogador.
   * docs/01-regras-do-duelo.md secao 6.6
   *
   * Gatilho INVOLUNTARIO — nao abre janela de resposta, ao contrario de `ON_SUMMON` (que so
   * alcanca mao/magia-armadilha do ADVERSARIO de quem invocou). Este dispara para todo monstro
   * face-up em campo dos DOIS lados, e existe porque `ON_SUMMON` nao serve pra carta que reage a
   * propria invocacao do controlador (leva_sdk_sdy/curadoria, Mysterious Puppeteer). Nao dispara
   * para Invocacao-Especial (Fusao inclusa) nem para baixar virado.
   */
  'ON_ANY_SUMMON',
  /**
   * O proprio monstro, face-up, mudado pelo jogador de Ataque para Defesa (ou o contrario). INVOLUNTARIO; nem a
   * Invocacao por Virada nem a mudanca por efeito contam. docs/01-regras-do-duelo.md secao 6.6 (`leva_mrd/curadoria`)
   */
  'ON_CHANGED_TO_DEFENSE',
  'ON_CHANGED_TO_ATTACK',
  /**
   * O proprio monstro INFLIGIU dano de batalha: o do lado que nao tomou o dano, atacante ou defensor.
   * docs/01-regras-do-duelo.md secoes 5.2 e 6.6 (`leva_mrd/curadoria`, Masked Sorcerer)
   *
   * INVOLUNTARIO e obrigatorio. So com perda real de PV, e nunca com o duelo encerrado pela batalha. A Continua
   * face-up do controlador dele tambem o ouve, para todo monstro desse lado (Robbin' Goblin); noutra Magia ou
   * Armadilha o carregamento o recusa.
   */
  'ON_BATTLE_DAMAGE_DEALT',
  /**
   * A propria carta chegou ao cemiterio do dono, de qualquer lugar (campo ou mao). Resolve no FIM da acao, sem janela
   * nem aviso de ativacao, e so se ela continuar la. docs/01-regras-do-duelo.md secao 6.6 (The Immortal of Thunder,
   * Sword of Deep-Seated, `leva_mrd/curadoria`)
   *
   * A segunda excecao a "efeito nao dispara efeito" (docs/02 secao 3.5): dispara venha de onde vier a ida.
   */
  'ON_SENT_TO_GRAVEYARD',
  /**
   * O proprio monstro DECLAROU um ataque, a monstro ou direto. docs/01-regras-do-duelo.md secoes 5.1 e 6.6 (Jirai Gumo,
   * `leva_mrd/curadoria`)
   *
   * INVOLUNTARIO e obrigatorio: resolve depois do `ATTACK_DECLARED` e ANTES da janela do oponente, sem corrente. Nao e o
   * `ON_ATTACK_DECLARED`, que e a janela de quem e atacado. So em monstro.
   */
  'ON_THIS_CARD_ATTACKS',
  /**
   * Um monstro SERIA Invocado: a janela da tentativa, entre o monstro entrar na zona e o resto da Invocacao acontecer.
   * docs/01-regras-do-duelo.md secao 4.5 (Solemn Judgment, Horn of Heaven, `leva_mrd/curadoria`, ADR 0050)
   *
   * O outro gatilho da Armadilha Contra, e so dela: Invocacao-Normal face-up, por Virada, Fusao e Ritual. Vale so no
   * ELO 1 dessa corrente; dali em diante a Contra responde ao elo (`ON_CARD_ACTIVATED`).
   */
  'ON_SUMMON_ATTEMPT',
] as const
export type EffectTrigger = (typeof EFFECT_TRIGGERS)[number]

export const EFFECT_TARGETS = [
  'SELF',
  'OPPONENT',
  /**
   * Um jogador escolhido na ativacao. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * A escolha traz o `playerId`, e nao um `instanceId` — e o unico alvo `_CHOICE` assim. Existe
   * para o texto "o jogador a sua escolha", que `SELF` e `OPPONENT` so conseguem aproximar.
   */
  'PLAYER_CHOICE',
  'SELF_MONSTER_CHOICE',
  'OPPONENT_MONSTER_CHOICE',
  /** Um monstro de QUALQUER dos dois campos, face-up ou face-down. */
  'ANY_MONSTER_CHOICE',
  /**
   * Um monstro FACE-UP de qualquer dos dois campos. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * O irmao de `ANY_MONSTER_CHOICE` que exclui o virado — para o texto "1 monstro com a face
   * para cima no campo", que oferecer o virado como alvo legal aproximaria: o jogador nao sabe
   * o que ha por baixo, e a carta nao alcanca isso.
   */
  'ANY_FACE_UP_MONSTER_CHOICE',
  /**
   * Um monstro do OPONENTE, face-up e em Posicao de Ataque, a sua escolha. docs/02-catalogo-de-cartas.md secao 3.2
   * (Block Attack, `leva_mrd/curadoria`)
   */
  'OPPONENT_FACE_UP_ATTACK_MONSTER_CHOICE',
  'ALL_OPPONENT_MONSTERS',
  /**
   * Todos os monstros do oponente em POSICAO DE ATAQUE, e so eles.
   * docs/02-catalogo-de-cartas.md secao 3.2
   *
   * Derivado do campo, como `ALL_OPPONENT_MONSTERS`: nao consome escolha. Monstro em Defesa
   * fica de fora — e a diferenca entre a armadilha que pune quem ataca e a que limpa o campo.
   */
  'ALL_OPPONENT_ATTACK_POSITION_MONSTERS',
  /** Todos os monstros do PROPRIO campo. O espelho de `ALL_OPPONENT_MONSTERS`. */
  'ALL_SELF_MONSTERS',
  'ALL_MONSTERS',
  /**
   * Toda carta da MAO dos DOIS jogadores, sem escolha nenhuma (nem `atMost`, nem selecao do
   * jogador). docs/02-catalogo-de-cartas.md secao 3.2
   *
   * DESVIO DELIBERADO do padrao de alvo em massa: `ALL_MONSTERS`/`ALL_OPPONENT_MONSTERS`/etc.
   * fizzlam a carta inteira com campo vazio — aqui, mao vazia (de um lado, ou dos dois) NUNCA
   * fizzla. Card Destruction (`leva_sdk_sdy/curadoria`) afeta os dois lados independentemente: mao
   * vazia de um jogador nao apaga o efeito do outro. docs/01-regras-do-duelo.md secao 6.3
   */
  'ALL_HAND_CARDS',
  /**
   * Toda carta de Magia e Armadilha no campo, dos DOIS jogadores: as cinco zonas de magia, viradas ou nao, e as duas
   * Magias de Campo. A propria carta fica de fora. docs/02-catalogo-de-cartas.md secao 3.2 (Heavy Storm,
   * `leva_mrd/curadoria`)
   *
   * O unico alvo de zona de magia que alcanca a Magia de Campo: no TCG a Zona de Campo e parte da area de Magia e
   * Armadilha.
   */
  'ALL_SPELL_TRAP_CARDS',
  /**
   * Toda carta FACE-UP das zonas de magia dos DOIS jogadores cujo id esta em `cardIds`, da acao — todas as copias.
   * docs/02-catalogo-de-cartas.md secao 3.2 (Dragon Piper, `leva_mrd/curadoria`)
   *
   * A virada fica de fora: destrui-la pelo id revelaria quem ela e. O nome vive no conteudo, nunca no motor.
   */
  'ALL_FACE_UP_SPELL_TRAP_WITH_CARD_IDS',
  /**
   * Todos os monstros FACE-UP dos dois campos; `monsterType`, opcional, restringe a um tipo.
   * docs/02-catalogo-de-cartas.md secao 3.2 (Dragon Piper, Shield & Sword, `leva_mrd/curadoria`)
   *
   * Executado, e nao consultado: diferente de `ALL_MONSTERS_OF_TYPE`, nunca le o tipo de carta oculta nem a vira.
   */
  'ALL_FACE_UP_MONSTERS',
  /**
   * O monstro face-up do oponente com o MENOR ATK efetivo (docs/01 secao 3.4).
   *
   * Consome escolha, e a escolha alcanca SO os EMPATADOS no menor ATK — "em caso de empate, voce
   * escolhe". Sem empate ha um candidato so, e com escolha ausente (ou em gatilho involuntario)
   * vale o criterio de docs/01 secao 6.6, que e o piso.
   */
  'OPPONENT_LOWEST_ATK_CHOICE',
  /**
   * Um monstro do OPONENTE, face-up ou virado, escolhido PELO OPONENTE na resolucao (Share the Pain,
   * `leva_mrd/curadoria`). docs/02-catalogo-de-cartas.md secao 3.2, ADR 0049
   *
   * Nao e alvo: fica fora de `PROTECTABLE_MONSTER_TARGETS` e nao consome os `targets` de quem ativou. A escolha chega
   * pela pausa antes do elo; sem ela, vale o de MENOR ATK efetivo (docs/01 secao 6.6). So em `TRIBUTE`, nas `actions`
   * de uma Magia Normal.
   */
  'OPPONENT_MONSTER_OPPONENT_CHOICE',
  /**
   * Uma carta face-up na ZONA DE MAGIA, de qualquer dos dois lados.
   * docs/02-catalogo-de-cartas.md secao 3.2
   *
   * A Magia de Campo fica fora: a zona dela e outra, e a operacao dela e `DESTROY_FIELD`
   * (docs/01 secao 6.2).
   */
  'ANY_FACE_UP_SPELL_CHOICE',
  /**
   * Idem, INCLUINDO carta virada para baixo. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * O `category` da acao NAO filtra este alvo: a categoria de uma carta virada e oculta, e
   * descobri-la e o que a carta faz — o filtro e avaliado depois de revelar.
   */
  'ANY_SPELL_CHOICE',
  'ATTACKING_MONSTER',
  'DEFENDING_MONSTER',
  'SUMMONED_MONSTER',
  'EQUIPPED_MONSTER',
  /**
   * Uma ou mais cartas da PROPRIA mao, a escolha do jogador. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * `monsterType` na acao restringe as candidatas a monstro daquele tipo (The Flute of Summoning
   * Dragon, `leva_sdk_sdy/curadoria`: "ate 2 Dragao da mao"), e `category` a uma categoria (The
   * Cheerful Coffin, `leva_mrd/curadoria`: "Cards de Monstro") — ausentes, qualquer carta da mao serve.
   * `atMost` funciona aqui do mesmo jeito que em `BANISH`: o jogador escolhe de 1 a `count`.
   */
  'SELF_HAND_CHOICE',
  /**
   * Uma carta do CEMITERIO DO OPONENTE. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * O unico alvo que olha para fora do campo. O `category` da acao o filtra: o cemiterio tem
   * monstro, magia e armadilha misturados.
   */
  'OPPONENT_GRAVEYARD_CHOICE',
  /**
   * Um MONSTRO de QUALQUER dos dois cemiterios. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * O espelho de `OPPONENT_GRAVEYARD_CHOICE`, dos dois lados e so monstro. Existe para o texto
   * "escolha 1 monstro em qualquer Cemiterio".
   */
  'ANY_GRAVEYARD_MONSTER_CHOICE',
  /**
   * Uma ou mais cartas de QUALQUER dos dois cemiterios, sem as banidas (Soul Release, `leva_mrd/curadoria`).
   * docs/02-catalogo-de-cartas.md secao 3.2
   *
   * `category` opcional. A ordem dos candidatos e o cemiterio do OPONENTE primeiro, a mais recente primeiro: o
   * desempate de docs/01 secao 6.6 para quem atravessa os dois lados. So em `BANISH`.
   */
  'ANY_GRAVEYARD_CHOICE',
  /**
   * Todos os monstros de um TIPO, nos dois campos. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * O tipo vem de `monsterType` na acao. Alvo de carta Continua: e consultado, e por isso nao
   * consome escolha nenhuma.
   */
  'ALL_MONSTERS_OF_TYPE',
  /**
   * Os monstros de um ATRIBUTO nos dois campos, com `attribute` na acao. So consultado, em `MODIFY_ATK`/`MODIFY_DEF`.
   * docs/02-catalogo-de-cartas.md secao 3.3 (Milus Radiant e as outras cinco, `leva_mrd/curadoria`)
   */
  'ALL_MONSTERS_OF_ATTRIBUTE',
  'THIS_CARD',
  /**
   * Um monstro do PROPRIO Deck, a escolha do jogador, filtrado por `atkAtMost`, `defAtMost` e `cardIds`.
   * docs/02-catalogo-de-cartas.md secao 3.2 (Last Will, `leva_sdk_sdy/curadoria`; a busca, Thunder Dragon,
   * `leva_mrd/curadoria`)
   *
   * SO existe em `SPECIAL_SUMMON` e em `ADD_TO_HAND`, e SO em carta que tambem declare `SHUFFLE_DECK` depois
   * (docs/01-regras-do-duelo.md secao 4.2): tirar carta do Deck exige embaralhar de volta na MESMA
   * resolucao. O Deck do dono e sempre oculto (docs/05-protocolo-tempo-real.md secao 4) — a escolha
   * NAO chega pelo `targets` da ativacao, como `SELF_HAND_CHOICE`; o servidor pausa ANTES da
   * ativacao comecar, mostra so ao dono os candidatos ja filtrados por `atkAtMost`, e refaz a
   * ativacao com o `instanceId` escolhido.
   */
  'SELF_DECK_CHOICE',
  /**
   * Uma carta da MAO DO OPONENTE, escolhida ÀS CEGAS — sem ver o conteudo antes de escolher.
   * docs/02-catalogo-de-cartas.md secao 3.2 (The Inexperienced Spy, `leva_sdk_sdy/curadoria`)
   *
   * Diferente de `SELF_HAND_CHOICE`: a mao do OPONENTE e sempre oculta para quem ativa (docs/05
   * secao 4), entao a escolha nao chega pelo `targets` da propria ativacao — o servidor pausa
   * ANTES da ativacao comecar (`openHandChoice`, `actions.ts`), mostra so a quem escolhe os
   * `instanceId` de cada carta da mao do adversario (NUNCA o `cardId` — quem escolhe aponta a
   * posicao sem saber o que ha nela, e descobrir e o que a carta faz), e refaz a ativacao com o
   * `instanceId` escolhido. Mesmo mecanismo de `SELF_DECK_CHOICE`, mas sem `candidateCardIds`:
   * aqui a identidade nunca viaja, nem para quem escolhe.
   */
  'OPPONENT_HAND_CHOICE',
  /**
   * Uma carta da MAO DO OPONENTE, sorteada pelo motor ao resolver. docs/02-catalogo-de-cartas.md secao 3.2
   * (`leva_mrd/curadoria`, White Magical Hat)
   *
   * Ninguem escolhe e nada para, ao contrario de `OPPONENT_HAND_CHOICE`. O sorteio consome o gerador
   * semeado so na resolucao, nunca ao conferir se a carta e jogavel (docs/01 secao 9) — por isso so
   * existe em `DISCARD`, e nunca em `cost`.
   */
  'OPPONENT_HAND_RANDOM',
  /**
   * Uma carta do PROPRIO cemiterio, a sua escolha, filtrada por `category`, sem as banidas. docs/02-catalogo-de-cartas.md
   * secao 3.2 (Magician of Faith, Mask of Darkness, `leva_mrd/curadoria`). No criterio automatico, a mais recente.
   */
  'SELF_GRAVEYARD_CHOICE',
  /**
   * As `count` cartas do TOPO do proprio Deck (ou menos, se o Deck tiver menos), para por de volta na ordem escolhida
   * (Big Eye, `leva_mrd/curadoria`). docs/02-catalogo-de-cartas.md secao 3.2
   *
   * A escolha e uma permutacao delas, a primeira no topo. O Deck e oculto: os candidatos chegam so a quem escolhe, com o
   * `cardId`, na pausa da virada (docs/05 secao 4.2). So em `REORDER_DECK_TOP`.
   */
  'SELF_DECK_TOP_CHOICE',
  /**
   * Um monstro da PROPRIA mao ou do PROPRIO Deck, filtrado pelo `cardIds` da acao (Elegant Egotist, ADR 0053).
   * docs/02-catalogo-de-cartas.md secao 3.2
   *
   * So em `SPECIAL_SUMMON` de `ON_ACTIVATE`, com `cardIds`, em carta que feche com `SHUFFLE_DECK` depois — embaralhando
   * sempre, venha a escolhida da mao ou do Deck. A escolha e a pausa PRE-ativacao, com o passo `HAND_OR_DECK`: as
   * candidatas das duas origens numa lista so, com `candidateCardIds`, so para o dono (docs/05 secao 4.2).
   */
  'SELF_HAND_OR_DECK_CHOICE',
  /**
   * Um monstro FACE-UP do PROPRIO campo, filtrado pelo `cardIds` da acao, que ainda nao tem Equipamento-monstro ligado
   * (Cocoon of Evolution, ADR 0053). docs/02-catalogo-de-cartas.md secao 3.2
   *
   * So em `EQUIP_THIS_CARD`. E escolha: a `PREVENT_TARGETING` o alcanca.
   */
  'SELF_FACE_UP_MONSTER_CHOICE',
] as const
export type EffectTarget = (typeof EFFECT_TARGETS)[number]

export const EFFECT_OPS = [
  /**
   * Anula o elo ANTERIOR da corrente: ele nao resolve e vai ao cemiterio.
   * docs/01-regras-do-duelo.md secao 6.5
   *
   * Nao se confunde com `NEGATE_ATTACK`, que cancela o ATAQUE. Esta cancela uma CARTA.
   */
  'NEGATE_CARD',
  /**
   * Nega a INVOCACAO que abriu a janela da tentativa: o monstro e destruido sem disparar nada, e o resto da Invocacao
   * nao acontece. Sem alvo, so em Armadilha Contra e so em `ON_SUMMON_ATTEMPT`. docs/01-regras-do-duelo.md secao 4.5
   */
  'NEGATE_SUMMON',
  'DAMAGE_LP',
  'HEAL_LP',
  'DESTROY_MONSTER',
  /**
   * Destroi uma carta face-up da ZONA DE MAGIA. docs/01-regras-do-duelo.md secao 6.2
   *
   * Nao alcanca a Magia de Campo (`DESTROY_FIELD` e a operacao dela) e nao ATIVA a carta
   * destruida: sair da zona por destruicao nao resolve efeito nenhum.
   */
  'DESTROY_SPELL_TRAP',
  'MODIFY_ATK',
  'MODIFY_DEF',
  'NEGATE_ATTACK',
  'CHANGE_FIELD',
  'DESTROY_FIELD',
  'DRAW_CARD',
  'RETURN_TO_HAND',
  'CHANGE_POSITION',
  'DISCARD',
  'REVEAL_CARDS',
  /**
   * Encerra o duelo com vitoria do jogador alvo. docs/01-regras-do-duelo.md secao 10
   *
   * A UNICA operacao que termina o duelo, e a unica cujo efeito nada desfaz: o estado fica
   * `FINISHED` no mesmo passo, e nada resolve depois.
   */
  'WIN_DUEL',
  /**
   * Vira um MONSTRO com a face para cima, e dispara o `ON_FLIP` dele.
   * docs/01-regras-do-duelo.md secao 6.2
   *
   * E a unica excecao a "efeito nao dispara efeito" (docs/02 secao 3.5), e o freio dela e
   * profundidade um: uma virada aninhada nao executa.
   */
  'FLIP_FACE_UP',
  /**
   * Tira carta do cemiterio e a poe em "removido do jogo". docs/01-regras-do-duelo.md secao 3.0
   *
   * A unica operacao que alcanca o cemiterio, e a unica cuja contagem pode ser MAXIMA — ver
   * `atMost` em `effectActionSchema`.
   */
  'BANISH',
  /**
   * CONSULTADA: os monstros alcancados nao declaram ataque.
   * docs/01-regras-do-duelo.md secoes 5.1 e 6.0
   *
   * A unica alteracao de REGRA que uma Continua faz hoje, e o conjunto delas e fechado. So existe
   * com o gatilho `WHILE_FACE_UP`; o loader recusa qualquer outra combinacao.
   */
  'PREVENT_ATTACK',
  /**
   * Invocacao-Especial: poe em campo um monstro que esta FORA do campo.
   * docs/01-regras-do-duelo.md secao 4.2
   *
   * Nao consome a Invocacao-Normal do turno e ignora as regras de Tributo. O alvo diz de ONDE o
   * monstro vem — mao (`SELF_HAND_CHOICE`) ou cemiterio —, e nao um campo de origem: o alvo ja
   * carrega essa informacao, e dois lugares dizendo a mesma coisa e um lugar para divergir.
   */
  'SPECIAL_SUMMON',
  /**
   * O jogador alvo NAO sofre dano nesta batalha. docs/01-regras-do-duelo.md secao 5.2
   *
   * Nao se confunde com `NEGATE_ATTACK`: ali a batalha nao acontece; aqui ela acontece inteira, o
   * monstro morre, e so o dano nao passa. `duration` OPCIONAL, reaproveitando `EFFECT_DURATIONS`:
   * ausente, protege so a batalha PENDENTE da resposta em curso (Kuriboh); `END_OF_TURN`
   * (`leva_sdk_sdy/curadoria`, Waboku) protege TODAS as batalhas do jogador alvo pelo resto do
   * turno, por uma marca de duelo (`DuelState.battleDamagePreventedForPlayerIds`), ao lado da
   * marca local — as duas protegem, uma nao substitui a outra.
   */
  'PREVENT_BATTLE_DAMAGE',
  /**
   * Lanca a moeda. docs/01-regras-do-duelo.md secao 9
   *
   * A UNICA operacao aleatoria da DSL, e ela nao decide nada sozinha: sorteia, compara com o lado
   * que o jogador chamou na acao, e escreve ganhou-ou-perdeu no contexto do gatilho. Quem age sao
   * os efeitos SEGUINTES da mesma carta, filtrados por `COIN_FLIP_WON`/`COIN_FLIP_LOST`.
   *
   * Um sorteio por ativacao: os dois ramos leem o MESMO resultado.
   *
   * Com `count`, sao `count` moedas na mesma rodada, e o contexto guarda quantas deram cara, para
   * `COIN_HEADS_AT_LEAST`. So ha chamada quando o gatilho da carta le `COIN_FLIP_WON`/`COIN_FLIP_LOST` (Barrel Dragon,
   * `leva_mrd/curadoria`, docs/01 secao 6.2).
   */
  'COIN_FLIP',
  /**
   * CONSULTADA: os monstros alcancados ficam em Posicao de Defesa.
   * docs/01-regras-do-duelo.md secoes 4.1 e 6.0
   *
   * A SEGUNDA alteracao de regra continua, ao lado de `PREVENT_ATTACK`, no mesmo conjunto fechado.
   * Consultada, e nao aplicada uma vez: um monstro invocado DEPOIS tambem deita.
   */
  'FORCE_DEFENSE',
  /**
   * CONSULTADA: os monstros alcancados nao podem ser ESCOLHIDOS COMO ALVO por nenhum efeito de
   * carta. docs/01-regras-do-duelo.md secao 6.0
   *
   * A TERCEIRA alteracao de regra continua, no mesmo conjunto fechado de `PREVENT_ATTACK` e
   * `FORCE_DEFENSE`. So existe com o gatilho `WHILE_FACE_UP`; o loader recusa qualquer outra
   * combinacao. Ao contrario das duas irmas, tambem existe num efeito de MONSTRO: um monstro
   * face-up em campo ja esta no estado "continuo" por natureza, sem precisar de
   * `spellKind`/`trapKind` nenhum. Protege o alvo que ESCOLHE um monstro em campo — com ou sem
   * lista de candidatos (ver `PROTECTABLE_MONSTER_TARGETS`) — e nao o efeito em massa, que
   * continua alcancando normalmente.
   */
  'PREVENT_TARGETING',
  /**
   * Toma o controle de 1 monstro do OPONENTE ate a Fase Final do turno de quem ativou.
   * docs/01-regras-do-duelo.md secao 4.2, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * Diferente de `SPECIAL_SUMMON`: o monstro NAO sai do campo, so migra de array — mesma
   * instancia, mesma `position`/`faceDown`/`modifiers`, sem resetar `hasAttacked`. Reaproveita
   * `ownerId` (`MonsterOnField`) do mesmo jeito que a Invocacao-Especial do cemiterio alheio, e
   * expira junto com os modificadores `END_OF_TURN` — mesmo instante, resolvido no handler de
   * `END_TURN`. Zona do dono ocupada no retorno manda a carta ao Cemiterio dele, sem travar.
   */
  'TAKE_CONTROL',
  /**
   * DA o proprio monstro (`THIS_CARD`) ao oponente de quem ativou, para sempre: sem prazo, sem `borrowed` e sem volta.
   * Zona de mesmo indice no campo do oponente, se livre; senao a menor livre; sem nenhuma, fizzla. O monstro nao sai do
   * campo, e os Equipamentos continuam ligados. Emite `CONTROL_GIVEN`. So nas `actions` de um `ON_MONSTER_EFFECT` de
   * monstro. docs/01-regras-do-duelo.md secao 4.2 (Mushroom Man #2, `leva_mrd/curadoria`, ADR 0052)
   */
  'GIVE_CONTROL',
  /**
   * SEM ALVO, como `COIN_FLIP`/`DESTROY_FIELD`/`NEGATE_ATTACK`: efeito GLOBAL, dos dois jogadores.
   * docs/01-regras-do-duelo.md secao 3.4, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * Ate a Fase Final, inverte o sinal da soma dos modificadores FIXOS (`MonsterModifier`, o que
   * `MODIFY_ATK`/`MODIFY_DEF` aplicam) no calculo de ATK/DEF efetivo — nunca o ajuste de terreno,
   * e nunca o bonus de Continua. Retroativo por construcao: a inversao mora no calculo
   * (`effectiveAtk`/`effectiveDef`), nao em cada modificador, entao todo `MonsterModifier` ja
   * existente e todo aplicado depois, enquanto a flag durar, somam invertidos igual. Expira no
   * mesmo handler de `END_TURN` que ja expira `MonsterModifier` com `duration: END_OF_TURN` —
   * mesma disciplina de `controlUntilEndOfTurn` (`TAKE_CONTROL`).
   */
  'REVERSE_ATK_DEF_MODIFIERS',
  /**
   * Ate a Fase Final, troca o ATK ORIGINAL com a DEF ORIGINAL de cada monstro alcancado (so `ALL_FACE_UP_MONSTERS`).
   * docs/01-regras-do-duelo.md secao 3.4 (Shield & Sword, `leva_mrd/curadoria`)
   *
   * Marca por monstro, e nao flag de duelo: quem chega ao campo depois fica de fora. ALTERNA — no ja trocado, destroca.
   * Os modificadores ficam onde estavam.
   */
  'SWAP_ORIGINAL_ATK_DEF',
  /**
   * CONSULTADA: o controlador pode fazer 1 Invocacao-Normal ou Set EXTRA no turno, ao custo de
   * 500 Life Points. docs/01-regras-do-duelo.md secao 4.4, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * A QUARTA alteracao de regra continua, no mesmo conjunto fechado de `PREVENT_ATTACK`,
   * `FORCE_DEFENSE` e `PREVENT_TARGETING`. So existe com o gatilho `WHILE_FACE_UP`; o loader recusa
   * qualquer outra combinacao. SEM ALVO, como `COIN_FLIP`/`DESTROY_FIELD`/`REVERSE_ATK_DEF_MODIFIERS`:
   * e permissao de jogada para o PROPRIO controlador, nao efeito sobre carta ou jogador. Duas
   * reducoes deliberadas em relacao ao texto oficial (Ultimate Offering, `leva_sdk_sdy/curadoria`):
   * so no PROPRIO turno de quem controla a carta (nao no do oponente), e uma vez por turno (nao
   * ilimitada por pagamento repetido) — `PlayerState.extraNormalSummonUsedThisTurn` reseta junto
   * com `hasNormalSummoned`.
   */
  'GRANT_EXTRA_NORMAL_SUMMON',
  /**
   * CONSULTADA, so em Monstro: este monstro pode atacar diretamente com monstros do oponente em campo.
   * docs/01-regras-do-duelo.md secao 5.2 (Queen's Double e as outras cinco, `leva_mrd/curadoria`)
   */
  'ALLOW_DIRECT_ATTACK',
  /**
   * CONSULTADA, so em Monstro, sem alvo: declarar ataque com este monstro custa `value` PV ao controlador, com o piso
   * geral de custo (so com MAIS PV que o valor). docs/01-regras-do-duelo.md secao 5.1 (Dark Elf, `leva_mrd/curadoria`)
   *
   * Nao e `cost` de efeito: declarar ataque nao ativa nada. Pago na declaracao, nao volta se o ataque for anulado.
   */
  'ATTACK_LP_COST',
  /**
   * Os monstros do jogador alvo NAO sao destruidos em batalha, ate a Fase Final.
   * docs/01-regras-do-duelo.md secao 5.2, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * Irma de `PREVENT_BATTLE_DAMAGE` com `duration: END_OF_TURN`, mas SEMPRE "ate o fim do turno"
   * — sem a variante "so esta batalha", que nenhuma carta pede hoje (`leva_sdk_sdy/curadoria`,
   * Waboku). Liga `DuelState.battleDestructionPreventedForPlayerIds`: o monstro cujo DONO esta
   * na lista sobrevive a batalha (o resto da resolucao continua normal), e nao dispara
   * `ON_DESTROYED_BY_BATTLE`. Por jogador, nao pelo duelo inteiro.
   */
  'PREVENT_BATTLE_DESTRUCTION',
  /**
   * Escolhe 1 monstro do OPONENTE; pelo resto do turno, toda Invocacao por Tributo do controlador
   * precisa incluir esse monstro — mesmo sendo do oponente, "como se voce o controlasse" — e o
   * controlador NAO tem Fase de Batalha neste turno. docs/01-regras-do-duelo.md secao 4 e secao
   * 5.1, docs/02-catalogo-de-cartas.md secao 3.3 (Soul Exchange, `leva_sdk_sdy/curadoria`)
   *
   * DUAS consequencias de uma unica resolucao, deliberadamente uma operacao so (nao duas): as
   * duas nascem do MESMO alvo e tem que fizzlar JUNTAS quando ele nao existe (sem monstro do
   * oponente em campo, nem a restricao de Tributo nem a suspensao da Fase de Batalha valem) — duas
   * operacoes independentes correriam risco de uma resolver sem a outra.
   *
   * Liga `DuelState.mandatoryTributeInstanceId`/`mandatoryTributeOwnerId` (consultados no case
   * `SUMMON`, `actions.ts`, e na enumeracao de `legal-actions.ts`) e
   * `DuelState.battlePhaseSkippedThisTurn` (consultado em `nextPhaseFor`, MESMO mecanismo que ja
   * pula a Fase de Batalha do turno 1 do jogador inicial). Os tres expiram no mesmo handler de
   * `END_TURN` que ja expira `atkDefModifiersReversedUntilEndOfTurn` e as listas de Waboku.
   */
  'GRANT_MANDATORY_TRIBUTE',
  /**
   * Embaralha o Deck do alvo (`target: SELF` na curadoria atual).
   * docs/01-regras-do-duelo.md secao 4.2, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * Existe para fechar a excecao de `SPECIAL_SUMMON` com `SELF_DECK_CHOICE` (Last Will,
   * `leva_sdk_sdy/curadoria`): a UNICA razao pela qual o Deck pode ser origem de Invocacao-Especial
   * e que a MESMA resolucao embaralha de volta depois — sem parametro alem do alvo.
   */
  'SHUFFLE_DECK',
  /**
   * No `cost`: oferece 1 monstro do proprio campo como Tributo, sem destruir. docs/02-catalogo-de-cartas.md secao
   * 3.3 (Cannon Soldier, Catapult Turtle, The Little Swordsman of Aile, `leva_mrd/curadoria`). Nas `actions`, so com
   * `OPPONENT_MONSTER_OPPONENT_CHOICE`: o oponente oferece 1 monstro dele (Share the Pain).
   */
  'TRIBUTE',
  /**
   * O ATK do atacante vale 0 no calculo da batalha pendente, e so nela: substitui o ATK efetivo, sem gravar
   * modificador. So com `ATTACKING_MONSTER` e em `ON_ATTACK_DECLARED`. docs/01-regras-do-duelo.md secao 5.2
   * (Sanga of the Thunder, Kazejin, Suijin, `leva_mrd/curadoria`)
   */
  'ZERO_ATTACKER_ATK',
  /**
   * Encerra a Fase de Batalha do turno DEPOIS de a batalha em curso resolver por inteiro: a fase vai a Principal 2.
   * Marca da batalha, sem alvo, e so em `ON_DESTROYED_BY_BATTLE`. docs/01-regras-do-duelo.md secao 5.1 (The Unhappy
   * Maiden, `leva_mrd/curadoria`)
   */
  'END_BATTLE_PHASE',
  /**
   * Para a mao do dono: do cemiterio, com `SELF_GRAVEYARD_CHOICE`, ou do proprio Deck, com `SELF_DECK_CHOICE` (a busca,
   * Thunder Dragon, `leva_mrd/curadoria`). docs/02-catalogo-de-cartas.md secao 3.3
   */
  'ADD_TO_HAND',
  /**
   * A propria carta, ja no cemiterio, vai ao TOPO do Deck do dono. So em `ON_SENT_TO_GRAVEYARD`.
   * docs/02-catalogo-de-cartas.md secao 3.3 (Sword of Deep-Seated, `leva_mrd/curadoria`)
   */
  'RETURN_TO_DECK_TOP',
  /**
   * Poe as cartas do topo do proprio Deck de volta na ordem escolhida, sem evento: a ordem e oculta. So com
   * `SELF_DECK_TOP_CHOICE`, em `ON_FLIP`. docs/02-catalogo-de-cartas.md secao 3.3 (Big Eye, `leva_mrd/curadoria`)
   */
  'REORDER_DECK_TOP',
  /**
   * Cartas da propria mao para o FUNDO do Deck, na ordem escolhida — a ultima escolhida mais ao fundo —, cada uma com
   * `instanceId` novo. So com `SELF_HAND_CHOICE` e `anyNumber`. docs/02-catalogo-de-cartas.md secao 3.3 (Yado Karu,
   * `leva_mrd/curadoria`)
   */
  'RETURN_TO_DECK_BOTTOM',
  /**
   * Marca adiada no ATACANTE: no `attackerTurn`-esimo turno do jogador que declarou o ataque, contado depois do turno do
   * ataque, ele nao declara ataque. So em `ON_ATTACKED_AFTER_DAMAGE` de monstro, com `ATTACKING_MONSTER`.
   * docs/01-regras-do-duelo.md secoes 5.1 e 6.3 (Electric Lizard, `leva_mrd/curadoria`)
   */
  'DELAYED_PREVENT_ATTACK',
  /**
   * Marca adiada no ATACANTE: na Fase Final do `attackerTurn`-esimo turno do jogador que declarou o ataque, contado depois
   * do turno do ataque, ele e destruido, sem janela. Mesmo lugar da irma. docs/01-regras-do-duelo.md secao 6.3 (Steel
   * Scorpion, `leva_mrd/curadoria`)
   */
  'DELAYED_DESTROY',
  /**
   * CONSULTADA, so em Magia de Equipamento, com `WHILE_FACE_UP` e `EQUIPPED_MONSTER`, sem condicao: os monstros do
   * adversario de quem controla o Equipamento, se atacarem um monstro, tem de atacar o equipado — enquanto ele estiver no
   * campo desse jogador. O ataque direto continua disponivel a quem o tem. docs/01-regras-do-duelo.md secoes 5.1 e 6.0
   * (Ring of Magnetism, `leva_mrd/curadoria`)
   */
  'MANDATORY_ATTACK_TARGET',
  /**
   * "Ao invés disso, destrua este card": destroi a propria carta, e todo elo do OPONENTE abaixo dela na corrente deixa de
   * destruir as Armadilhas de quem a controla. Sem alvo, so nas `actions` de um `ON_CARD_ACTIVATED` de Armadilha Normal,
   * com `CHAIN_WOULD_DESTROY_OWN_TRAP`. docs/01-regras-do-duelo.md secao 6.5 (Fake Trap, `leva_mrd/curadoria`)
   */
  'REPLACE_OWN_TRAP_DESTRUCTION',
  /**
   * O contador de Esperas do proprio monstro (`THIS_CARD`), com teto `value`: anda +1 em cada Espera do controlador e para
   * no teto. Nas `actions` do `ON_FLIP`, nasce na virada com a contagem 0 (Castle of Dark Illusions); no `WHILE_FACE_UP`,
   * e CONSULTADO: nasce quando a condicao do efeito passa a valer e some quando ela deixa de valer (Pumpking the King of
   * Ghosts). Lido pelo `perCount: THIS_CARD_TURN_COUNTER`. docs/01-regras-do-duelo.md secao 6.0, item 8
   */
  'TURN_COUNTER',
  /**
   * A PROPRIA carta de monstro, da mao, vira Equipamento do alvo (`SELF_FACE_UP_MONSTER_CHOICE`, com `cardIds`): vai a
   * menor zona de magia livre, face-up, ligada a ele ja na ativacao, e e Magia de Equipamento para tudo que le a zona; no
   * cemiterio volta a ser monstro. Ao resolver, nasce na carta o contador de turnos do procedimento (`turnCounter`,
   * contagem 0), que anda na Espera de quem a controla, SEM teto — `value` e so a referencia da ficha. So num
   * `ON_MONSTER_EFFECT` de monstro, sem custo e sem `phase` (Cocoon of Evolution). docs/01-regras-do-duelo.md secoes 6 e
   * 6.1, ADR 0053
   */
  'EQUIP_THIS_CARD',
  /**
   * CONSULTADA: enquanto a carta estiver ligada pelo `EQUIP_THIS_CARD` dela (resolvido, com o contador), o ATK e a DEF
   * ORIGINAIS do equipado sao os impressos desta carta — antes da troca do `SWAP_ORIGINAL_ATK_DEF`. So em `WHILE_FACE_UP`
   * com `EQUIPPED_MONSTER`, num monstro que tenha `EQUIP_THIS_CARD` (Cocoon of Evolution). docs/01 secao 3.4, ADR 0053
   */
  'SET_ORIGINAL_ATK_DEF',
] as const
export type EffectOp = (typeof EFFECT_OPS)[number]

export const EFFECT_DURATIONS = ['PERMANENT', 'END_OF_TURN', 'WHILE_EQUIPPED'] as const
export type EffectDuration = (typeof EFFECT_DURATIONS)[number]

/**
 * De onde `DAMAGE_LP` tira o valor quando ele e DERIVADO DO CAMPO, e nao um numero.
 * docs/02-catalogo-de-cartas.md secao 3.3
 *
 * Conjunto FECHADO, como `REVEAL_SOURCES`. `HALF_TOTAL_ATK_OF_SELF_MONSTERS`: metade da soma do
 * ATK EFETIVO dos proprios monstros, arredondada para baixo. `OPPONENT_MONSTER_COUNT_X_500`:
 * quantos monstros o OPONENTE controla (todo slot ocupado, sem filtro de posicao/virado) vezes
 * `DAMAGE_PER_OPPONENT_MONSTER` (Just Desserts, `leva_sdk_sdy/curadoria`). Um `value` fixo que
 * significasse "metade da soma de uma lista" ou "contagem vezes 500" seria numero magico com
 * fantasia de dado.
 */
export const DAMAGE_VALUE_SOURCES = [
  'HALF_TOTAL_ATK_OF_SELF_MONSTERS',
  'OPPONENT_MONSTER_COUNT_X_500',
  /** As Magias e Armadilhas do OPONENTE no campo, viradas e a de Campo inclusive (Princess of Tsurugi, `leva_mrd`). */
  'OPPONENT_SPELL_TRAP_COUNT_X_500',
  /** Metade do ATK que o Tributo do custo tinha no campo (Catapult Turtle, `leva_mrd`). docs/02 secao 3.3 */
  'HALF_ATK_OF_TRIBUTED',
  /** Metade dos PV do controlador ao resolver, para baixo; so com `target: SELF` (Jirai Gumo, `leva_mrd`). docs/02 secao 3.3 */
  'HALF_SELF_LP',
] as const
export type DamageValueSource = (typeof DAMAGE_VALUE_SOURCES)[number]

/**
 * De onde `REVEAL_CARDS` retira as cartas. `TOP_OF_DECK`: topo do Deck do alvo (`target`/`count`
 * normais). `ENTIRE_FIELD` (The Stern Mystic, `leva_sdk_sdy/curadoria`): toda carta virada para
 * baixo em campo, monstro e magia/armadilha, dos DOIS jogadores — sem `target`/`count`, o campo
 * inteiro nao e "o Deck de um jogador". docs/02-catalogo-de-cartas.md secao 3.3
 */
export const REVEAL_SOURCES = ['TOP_OF_DECK', 'ENTIRE_FIELD'] as const
export type RevealSource = (typeof REVEAL_SOURCES)[number]

/**
 * De onde `DRAW_CARD` tira a CONTAGEM quando ela e DERIVADA DA RESOLUCAO, e nao um numero fixo.
 * docs/02-catalogo-de-cartas.md secao 3.3
 *
 * Conjunto FECHADO, mesmo molde de `DAMAGE_VALUE_SOURCES`. Hoje um valor so: quantas `HAND_CARD`
 * aquele MESMO jogador perdeu por `DISCARD` nesta mesma resolucao de efeito (Card Destruction,
 * `leva_sdk_sdy/curadoria`). Exige que o `DISCARD` correspondente venha ANTES na lista de acoes —
 * mesma disciplina de ordem que `valueFrom` em `DAMAGE_LP`.
 */
export const DRAW_COUNT_SOURCES = ['MATCH_DISCARDED'] as const
export type DrawCountSource = (typeof DRAW_COUNT_SOURCES)[number]

/**
 * O que multiplica o `value` de um `MODIFY_ATK`/`MODIFY_DEF` consultado: o bonus e `value x contagem`, lida a cada
 * calculo, sempre do CONTROLADOR do monstro. docs/02-catalogo-de-cartas.md secao 3.3 (`leva_mrd/curadoria`)
 *
 * Formato diferente de `DAMAGE_VALUE_SOURCES` de proposito: la o numero e regra e vive em `constants.ts`; aqui ele
 * e da carta, e o `perCount` declara o que o multiplica.
 */
export const BONUS_COUNT_SOURCES = [
  /** Monstros face-up do proprio campo com id em `cardIds`, um por copia (Lava e Swamp Battleguard). */
  'SELF_FACE_UP_MONSTERS_WITH_CARD_IDS',
  /** Monstros no proprio cemiterio, sem os banidos (Shadow Ghoul). */
  'SELF_GRAVEYARD_MONSTERS',
  /** Cartas na propria mao (Muka Muka). */
  'SELF_HAND_CARDS',
  /**
   * As parcelas do contador de Esperas da CARTA DO EFEITO (`TURN_COUNTER`): `1 + contagem`, e zero sem contador. Nao conta
   * o campo do controlador, e por isso vale tambem num alvo em massa (Castle of Dark Illusions, Pumpking).
   * docs/01-regras-do-duelo.md secao 6.0, item 8
   */
  'THIS_CARD_TURN_COUNTER',
] as const
export type BonusCountSource = (typeof BONUS_COUNT_SOURCES)[number]

export const EFFECT_CONDITION_OPS = [
  'ALWAYS',
  'SELF_LP_LTE',
  'SELF_LP_GTE',
  'OPPONENT_LP_LTE',
  'ATTACKER_ATK_GTE',
  'ATTACKER_ATK_LTE',
  'SUMMONED_ATK_GTE',
  'SELF_MONSTER_COUNT_LTE',
  'OPPONENT_MONSTER_COUNT_GTE',
  'TERRAIN_IS',
  /** Tipo do monstro equipado (docs/02 secao 3.4). So faz sentido em Magia de Equipamento. */
  'EQUIPPED_TYPE_IS',
  /** A negacao: o equipado NAO e do tipo. docs/02 secao 3.4 (Germ Infection, Paralyzing Potion, `leva_mrd`) */
  'EQUIPPED_TYPE_IS_NOT',
  /**
   * A mao do controlador contem TODOS os `cardIds` declarados, contando duplicatas.
   * docs/02-catalogo-de-cartas.md secao 3.4
   *
   * A lista vive no DADO. Nenhum id de carta especifico entra no motor (docs/99-aviso-legal.md).
   */
  'HAS_CARD_IDS_IN_HAND',
  /**
   * ALGUM dos `cardIds` declarados esta face-up no PROPRIO campo (monstro OU magia/armadilha).
   * docs/02-catalogo-de-cartas.md secao 3.4
   *
   * Ao contrario de `HAS_CARD_IDS_IN_HAND` (multiconjunto, TODOS exigidos), aqui e OU: e a forma
   * de "esta carta nomeada precisa estar em campo pra voce ativar" (The Flute of Summoning
   * Dragon, `leva_sdk_sdy/curadoria`), que aceita nomes alternativos quando a lista tiver mais de
   * um. So o PROPRIO campo conta — decisao de curadoria, nao ambiguidade do texto oficial (que
   * nao especifica controlador). A lista vive no DADO, como em `HAS_CARD_IDS_IN_HAND`.
   */
  'HAS_CARD_IDS_ON_FIELD',
  /**
   * O `COIN_FLIP` desta ativacao saiu no lado que o jogador chamou (ou nao).
   * docs/02-catalogo-de-cartas.md secao 3.4
   *
   * Fora de uma ativacao com sorteio, as duas sao FALSAS — a mesma disciplina de `ATTACKER_ATK_*`
   * quando o gatilho nao traz o monstro de referencia.
   */
  'COIN_FLIP_WON',
  'COIN_FLIP_LOST',
  /**
   * Algum monstro do PROPRIO campo foi enviado ao cemiterio NESTE turno — batalha ou efeito, tanto
   * faz. docs/01-regras-do-duelo.md secao 4.2 (Last Will, `leva_sdk_sdy/curadoria`)
   *
   * Le `PlayerState.monsterSentToGraveyardThisTurn`, que reseta a cada inicio de turno junto com
   * `hasNormalSummoned`.
   */
  'SELF_MONSTER_SENT_TO_GRAVEYARD_THIS_TURN',
  /**
   * A Fase de Batalha AINDA NAO foi visitada neste turno. docs/01-regras-do-duelo.md secao 5.1
   * (Soul Exchange, `leva_sdk_sdy/curadoria`)
   *
   * "Voce nao pode conduzir sua Fase de Batalha no turno em que ativar este card": ativar DEPOIS
   * de atacar contornaria o preco da carta. Le `visitedPhasesThisTurn`, publico nas tres visoes.
   */
  'BATTLE_PHASE_NOT_CONDUCTED_THIS_TURN',
  /**
   * A carta do ELO ANTERIOR da corrente e do tipo `cardKind`. docs/01-regras-do-duelo.md secao 6.5
   * (Seven Tools of the Bandit, `leva_mrd/curadoria`)
   *
   * "Quando um Card de Armadilha for ativado": so em `ON_CARD_ACTIVATED`, e falsa fora de corrente.
   */
  'ACTIVATED_CARD_KIND_IS',
  /**
   * A carta foi invocada por Virada e saiu do campo direto para o cemiterio. So em `ON_SENT_TO_GRAVEYARD`.
   * docs/01-regras-do-duelo.md secao 6.6 (The Immortal of Thunder, `leva_mrd/curadoria`)
   */
  'THIS_CARD_WAS_FLIP_SUMMONED',
  /**
   * So no calculo da batalha em que o proprio monstro do efeito ataca um monstro do `attribute` da condicao; falsa em
   * toda outra consulta e no ataque direto. docs/01-regras-do-duelo.md secao 6.0, item 7 (Insect Soldiers of the Sky,
   * `leva_mrd/curadoria`)
   */
  'THIS_CARD_ATTACKS_ATTRIBUTE',
  /**
   * O `COIN_FLIP` desta ativacao deu pelo menos `value` caras. docs/02-catalogo-de-cartas.md secao 3.4 (Barrel Dragon,
   * `leva_mrd/curadoria`)
   *
   * A moeda sem chamada: nao se mistura com `COIN_FLIP_WON`/`COIN_FLIP_LOST` na mesma carta. Fora de uma ativacao com
   * sorteio, e FALSA.
   */
  'COIN_HEADS_AT_LEAST',
  /**
   * O monstro ATACANTE do gatilho esta em campo e NAO e do `monsterType`. Sem atacante, ou com ele fora de campo, e falsa.
   * So em `ON_ATTACKED_AFTER_DAMAGE`, e vista ANTES do aviso de ativacao. docs/02-catalogo-de-cartas.md secao 3.4
   * (Electric Lizard, Steel Scorpion, `leva_mrd/curadoria`)
   */
  'ATTACKER_TYPE_IS_NOT',
  /**
   * Algum elo do OPONENTE abaixo desta carta na corrente destruiria ao menos uma OUTRA Armadilha de quem a controla, ainda
   * em campo. Falsa fora de corrente. So em `ON_CARD_ACTIVATED` de Armadilha Normal. docs/02-catalogo-de-cartas.md secao
   * 3.4 (Fake Trap, `leva_mrd/curadoria`)
   */
  'CHAIN_WOULD_DESTROY_OWN_TRAP',
  /**
   * A carta chegou ao cemiterio VINDA DO CAMPO: destruida, tributada, material de Fusao. Descartada da mao, e falsa. So
   * em `ON_SENT_TO_GRAVEYARD`. docs/02-catalogo-de-cartas.md secao 3.4 (Witch of the Black Forest, Sangan,
   * `leva_mrd/curadoria`)
   */
  'THIS_CARD_WAS_SENT_FROM_FIELD',
  /**
   * ALGUM dos `cardIds` declarados esta face-up em QUALQUER dos dois campos (monstro OU magia/armadilha): a irma de
   * `HAS_CARD_IDS_ON_FIELD` para os dois lados. Consultavel. docs/02-catalogo-de-cartas.md secao 3.4 (Pumpking the King
   * of Ghosts, `leva_mrd/curadoria`)
   */
  'HAS_CARD_IDS_ON_ANY_FIELD',
  /**
   * A PROPRIA carta do efeito esta face-up numa zona de monstro, de qualquer lado. Na mao, e falsa. So no `ON_TURN_START`
   * de um monstro, e vista ANTES do aviso de ativacao: da mao, nada sai. docs/02-catalogo-de-cartas.md secao 3.4
   * (Mushroom Man #2, `leva_mrd/curadoria`, ADR 0052)
   */
  'THIS_CARD_FACE_UP_ON_FIELD',
] as const
export type EffectConditionOp = (typeof EFFECT_CONDITION_OPS)[number]

/**
 * Condicoes de ATIVACAO: quando uma delas e a condicao de um efeito `ON_ACTIVATE` e da FALSA, a
 * carta nao pode nem ser ativada — nao e so o efeito que fizzla na resolucao (docs/02-catalogo-de-
 * cartas.md secao 3.4, docs/01-regras-do-duelo.md secao 6).
 *
 * Conjunto FECHADO, e deliberadamente PEQUENO: no TCG real, o que faz uma condicao ser "de
 * ativacao" (bloqueia a carta) ou "de resolucao" (so decide se o efeito faz algo) e o TEXTO da
 * carta especifica, nunca a operacao em si — a MESMA operacao (`SELF_LP_LTE`, por exemplo) e
 * resolucao numa carta e ativacao em outra. Um conjunto fechado POR OPERACAO so e seguro quando a
 * operacao INTEIRA nasceu com esse proposito e nenhuma carta (real ou de teste) ja depende do
 * comportamento oposto. Achado ao curar The Flute of Summoning Dragon
 * (`leva_sdk_sdy/curadoria`): `SELF_LP_LTE` ja tinha uma carta de teste (`t-desperate`,
 * `effects.ts` __tests__) contando com o comportamento RESOLVE-ONLY — colocar `SELF_LP_LTE` (e
 * irmas: `SELF_LP_GTE`, `OPPONENT_LP_LTE`, `SELF_MONSTER_COUNT_LTE`, `OPPONENT_MONSTER_COUNT_GTE`,
 * `TERRAIN_IS`) neste conjunto quebraria essa carta. `HAS_CARD_IDS_IN_HAND`/`HAS_CARD_IDS_ON_FIELD`
 * entram porque NENHUMA carta (real ou de teste) as usa como resolve-only ainda, e o texto que
 * motivou a segunda ("carta nomeada precisa estar em campo pra voce ativar") so faz sentido como
 * ativacao. Se uma carta futura precisar de LP/contagem/terreno como ativacao de verdade, ela
 * exige uma marca PROPRIA por efeito (nao por operacao) — decisao pra quando aparecer.
 */
export const ACTIVATION_GATE_CONDITION_OPS: readonly EffectConditionOp[] = [
  'ALWAYS',
  'HAS_CARD_IDS_IN_HAND',
  'HAS_CARD_IDS_ON_FIELD',
  /**
   * Entra no gate por `leva_sdk_sdy/curadoria` (Last Will): "quando um monstro e enviado ao
   * cemiterio... voce pode ativar" e condicao de ATIVACAO no texto oficial, nao de resolucao — e,
   * como `HAS_CARD_IDS_ON_FIELD`, nenhuma carta (real ou de teste) ainda depende dela como
   * resolve-only.
   */
  'SELF_MONSTER_SENT_TO_GRAVEYARD_THIS_TURN',
  /**
   * Entra no gate por `leva_sdk_sdy/curadoria` (Soul Exchange): a carta nao pode nem ser ativada
   * depois da Fase de Batalha, e nenhuma carta depende dela como resolve-only.
   */
  'BATTLE_PHASE_NOT_CONDUCTED_THIS_TURN',
  /**
   * Entra no gate por `leva_mrd/curadoria` (Seven Tools of the Bandit): o tipo do elo anterior diz a
   * que a Contra PODE responder, e nao so o que ela faz ao resolver.
   */
  'ACTIVATED_CARD_KIND_IS',
]

export const effectConditionSchema = z.object({
  op: z.enum(EFFECT_CONDITION_OPS),
  value: z.number().int().optional(),
  terrain: z.enum(TERRAINS).optional(),
  monsterType: z.enum(MONSTER_TYPES).optional(),
  /** So para `HAS_CARD_IDS_IN_HAND`, `HAS_CARD_IDS_ON_FIELD` e `HAS_CARD_IDS_ON_ANY_FIELD`. docs/02 secao 3.4 */
  cardIds: z.array(z.string().min(1)).optional(),
  /** So para `ACTIVATED_CARD_KIND_IS`. docs/02-catalogo-de-cartas.md secao 3.4 */
  cardKind: z.enum(CARD_KINDS).optional(),
  /** So para `THIS_CARD_ATTACKS_ATTRIBUTE`: o atributo do monstro atacado. docs/02-catalogo-de-cartas.md secao 3.4 */
  attribute: z.enum(ATTRIBUTES).optional(),
})
export type EffectCondition = z.infer<typeof effectConditionSchema>

export const effectActionSchema = z.object({
  op: z.enum(EFFECT_OPS),
  target: z.enum(EFFECT_TARGETS).optional(),
  value: z.number().int().optional(),
  count: z.number().int().positive().optional(),
  duration: z.enum(EFFECT_DURATIONS).optional(),
  terrain: z.enum(TERRAINS).optional(),
  position: z.enum(['ATTACK', 'DEFENSE']).optional(),
  /** So para `REVEAL_CARDS`. docs/02-catalogo-de-cartas.md secao 3.3 */
  source: z.enum(REVEAL_SOURCES).optional(),
  /**
   * `count` vira "ate N": o jogador escolhe DE 1 A `count`. docs/02-catalogo-de-cartas.md secao 3.3
   *
   * O criterio automatico continua levando o maximo possivel. So nas `actions`, no ultimo alvo de escolha do
   * efeito. Sem esta marca, `count` continua exato, que e o que `DISCARD` e `DRAW_CARD` precisam.
   */
  atMost: z.boolean().optional(),
  /**
   * "Qualquer numero": de ZERO a mao inteira, sem `count` (Yado Karu, `leva_mrd/curadoria`). docs/02 secao 3.3
   *
   * So em `RETURN_TO_DECK_BOTTOM` com `SELF_HAND_CHOICE`, e nunca junto de `count`/`atMost`. O criterio automatico e
   * zero: e "voce pode".
   */
  anyNumber: z.boolean().optional(),
  /**
   * Filtro de categoria da carta alcancada. docs/02-catalogo-de-cartas.md secao 3.3
   *
   * Para `DESTROY_SPELL_TRAP`, `BANISH` e `ADD_TO_HAND`, e para o alvo `SELF_HAND_CHOICE` em qualquer
   * operacao; ausente, a operacao alcanca qualquer carta da zona.
   * O conjunto e o de `CARD_KINDS`, e o loader recusa a combinacao que nao faz sentido — um
   * `MONSTER` na zona de magia, por exemplo.
   */
  category: z.enum(CARD_KINDS).optional(),
  /**
   * So para `DAMAGE_LP`, no lugar de `value`: o dano e DERIVADO DO CAMPO.
   * docs/02-catalogo-de-cartas.md secao 3.3
   */
  valueFrom: z.enum(DAMAGE_VALUE_SOURCES).optional(),
  /**
   * So para `DRAW_CARD`, no lugar de `count`: a contagem e DERIVADA DA RESOLUCAO em andamento.
   * docs/02-catalogo-de-cartas.md secao 3.3
   */
  countFrom: z.enum(DRAW_COUNT_SOURCES).optional(),
  /**
   * So para `MODIFY_ATK`/`MODIFY_DEF` consultados em `THIS_CARD` (e, com `THIS_CARD_TURN_COUNTER`, em qualquer alvo
   * consultavel): o `value` vira a quantia POR UNIDADE dessa contagem. docs/02-catalogo-de-cartas.md secao 3.3
   */
  perCount: z.enum(BONUS_COUNT_SOURCES).optional(),
  /**
   * Os ids que `perCount: SELF_FACE_UP_MONSTERS_WITH_CARD_IDS` conta, ou que o alvo `ALL_FACE_UP_SPELL_TRAP_WITH_CARD_IDS`
   * alcanca. O nome vive no conteudo, nunca no motor.
   */
  cardIds: z.array(z.string().min(1)).optional(),
  /**
   * Filtro por tipo de monstro. docs/02-catalogo-de-cartas.md secao 3.2
   *
   * Em `ALL_MONSTERS_OF_TYPE` e OBRIGATORIO (sem ele o alvo nao alcanca ninguem). Em
   * `SELF_HAND_CHOICE` e OPCIONAL: restringe a escolha a monstro daquele tipo, ausente e
   * qualquer carta da mao serve. Em `ALL_FACE_UP_MONSTERS` tambem e OPCIONAL: ausente, alcanca todo face-up.
   */
  monsterType: z.enum(MONSTER_TYPES).optional(),
  /** So para `ALL_MONSTERS_OF_ATTRIBUTE`, e obrigatorio nele. docs/02-catalogo-de-cartas.md secao 3.3 */
  attribute: z.enum(ATTRIBUTES).optional(),
  /**
   * Teto de ATK das candidatas, em `SELF_DECK_CHOICE` e em `ANY_FACE_UP_MONSTER_CHOICE`.
   * docs/02-catalogo-de-cartas.md secao 3.2 (Last Will, `leva_sdk_sdy/curadoria`; Blast Juggler, `leva_mrd/curadoria`)
   *
   * No Deck, o ATK BASE: a carta ainda esta fora de campo, sem terreno nem modificador nenhum valendo — mesma leitura
   * de `atkOf` em `ATTACKER_ATK_*`, so que sobre o catalogo. No campo, o ATK EFETIVO (docs/01 secao 3.4): nos dois
   * casos, o ATK que a carta tem onde esta.
   */
  atkAtMost: z.number().int().min(0).optional(),
  /**
   * Teto de DEF BASE das candidatas, so em `SELF_DECK_CHOICE` (a busca, `leva_mrd/curadoria`). docs/02 secao 3.2
   */
  defAtMost: z.number().int().min(0).optional(),
  /**
   * So para `DESTROY_SPELL_TRAP` com alvo virado (`ANY_SPELL_CHOICE`). docs/02-catalogo-de-cartas.md
   * secao 3.3
   *
   * Quando o alvo virado NAO bate com `category`, a carta e revelada — a pergunta e pra QUEM.
   * Ausente (ou `false`): so quem ativou sabe (`base-213` Ceifador de Cards, `base-267` Remover
   * Armadilha). `true`: publica pros dois lados, porque o texto da carta diz so "revele-o", sem
   * restringir a quem (De-Spell, `leva_sdk_sdy/curadoria`). Nao muda nada quando o alvo bate — a
   * carta e destruida, e o resultado ja e publico por si so.
   */
  revealPublicly: z.boolean().optional(),
  /**
   * So nas marcas adiadas (`DELAYED_PREVENT_ATTACK`, `DELAYED_DESTROY`): o turno do jogador que declarou o ataque em que a
   * consequencia chega — 1 e o proximo turno dele, contado depois do turno do ataque. docs/01-regras-do-duelo.md secao
   * 6.3, docs/02-catalogo-de-cartas.md secao 3.3
   */
  attackerTurn: z.number().int().positive().optional(),
  /**
   * So no `ADD_TO_HAND` da busca (`SELF_DECK_CHOICE`): quem buscou nao ativa cartas com o NOME da carta buscada pelo resto
   * do turno — so ativacao declarada; os gatilhos involuntarios dela continuam. docs/01-regras-do-duelo.md secao 6.1
   * (Witch of the Black Forest, Sangan, `leva_mrd/curadoria`)
   */
  lockActivationThisTurn: z.boolean().optional(),
  /**
   * So no `EQUIP_THIS_CARD`: a habilidade que equipa a propria carta vale tambem com ela FACE-UP na zona de monstro de
   * quem e dono dela, e nao so na mao (regra da casa do Cocoon of Evolution, decisao do Paulo de 18/10/2026).
   * docs/01-regras-do-duelo.md secao 6.1, ADR 0053 secao 9
   */
  alsoFromField: z.boolean().optional(),
})
export type EffectAction = z.infer<typeof effectActionSchema>

/**
 * Limite de uso de um efeito de monstro. docs/02-catalogo-de-cartas.md secao 3
 *
 * `ONCE_WHILE_FACE_UP`: "uma vez enquanto este card estiver com a face para cima no campo". O uso fica marcado no
 * monstro em campo e some quando ele sai (Sanga of the Thunder, `leva_mrd/curadoria`).
 *
 * `ONCE_PER_TURN_BY_NAME`: "voce so pode usar este efeito de '{nome}' uma vez por turno". O uso e do jogador e do NOME,
 * gasto no disparo, e zera no comeco de cada turno (Witch of the Black Forest, Sangan, `leva_mrd/curadoria`).
 */
export const EFFECT_LIMITS = ['ONCE_WHILE_FACE_UP', 'ONCE_PER_TURN_BY_NAME'] as const
export type EffectLimit = (typeof EFFECT_LIMITS)[number]

/** Momentos de habilidade declarada fora da fase principal. docs/02-catalogo-de-cartas.md secao 3, ADR 0049 e 0052 */
export const EFFECT_PHASES = ['STANDBY', 'END'] as const
export type EffectPhase = (typeof EFFECT_PHASES)[number]

/**
 * Os tipos de Invocacao que abrem a janela `ON_SUMMON`. docs/01-regras-do-duelo.md secao 6.4
 *
 * `NORMAL` e a Invocacao-Normal face-up, a por Tributo e a extra (docs/01 secao 4.4) inclusas; `FLIP`, a por Virada;
 * `PROCEDURE`, o procedimento proprio (docs/01 secao 4.6). A Invocacao-Especial por efeito nao abre janela nenhuma.
 */
export const SUMMON_KINDS = ['NORMAL', 'FLIP', 'FUSION', 'RITUAL', 'PROCEDURE'] as const
export type SummonKind = (typeof SUMMON_KINDS)[number]

export const cardEffectSchema = z.object({
  trigger: z.enum(EFFECT_TRIGGERS),
  condition: effectConditionSchema.optional(),
  cost: z.array(effectActionSchema).optional(),
  actions: z.array(effectActionSchema).min(1),
  limit: z.enum(EFFECT_LIMITS).optional(),
  /**
   * O momento da habilidade declarada (`ON_MONSTER_EFFECT`): com `STANDBY`, so na Espera do proprio turno, e a Espera
   * para enquanto ela for legal (Blast Juggler, `leva_mrd/curadoria`); com `END`, so na Final do proprio turno, e o
   * `END_TURN` para na Final enquanto ela for legal (Mushroom Man #2). Ausente, a fase principal.
   * docs/02-catalogo-de-cartas.md secao 3, ADR 0049 e 0052
   */
  phase: z.enum(EFFECT_PHASES).optional(),
  /**
   * As Invocacoes a que o efeito `ON_SUMMON` responde, pelo texto da carta: so e elegivel na janela aberta por uma delas.
   * Obrigatorio em `ON_SUMMON`, recusado em outro gatilho (o loader confere). O Buraco Armadilha declara `NORMAL` e `FLIP`
   * (decisao do Paulo, 18/10/2026). docs/01-regras-do-duelo.md secao 6.4, docs/02-catalogo-de-cartas.md secao 3
   */
  summonKinds: z.array(z.enum(SUMMON_KINDS)).optional(),
})
export type CardEffect = z.infer<typeof cardEffectSchema>

// ---------------------------------------------------------------------------
// Cartas
// ---------------------------------------------------------------------------

const baseCardSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** A raridade de ESTREIA: depois que a carta existe no banco, a corrente e `Card.rarity` (ADR 0045). */
  rarity: z.enum(RARITIES),
  /**
   * Idem: o tipo de impressao de estreia. AUSENTE e `NORMAL` — opcional, e nao `.default`, para que
   * toda carta escrita a mao (fixture de teste, carta de bot) continue valendo sem saber que ele existe.
   */
  printType: z.enum(PRINT_TYPES).optional(),
  //
  // NAO ha `housePrice` aqui desde a ADR 0015. O preco de venda para a casa e estado
  // ADMINISTRAVEL, e vive em `Card.housePrice` no banco (docs/06 secao 5.1): mante-lo tambem aqui
  // faria a sincronizacao do catalogo apagar a curadoria de preco a cada publicacao — a tabela
  // `Card` e reescrita a cada `db:seed`, e o preco e a unica coluna dela que nao e espelho.
  //
  // O que o catalogo descreve e o que a carta E; quanto ela VALE e outra conversa, e ela tem tela.
  text: z.string().default(''),
  artId: z.string().min(1),
  retired: z.boolean().default(false),
})

/**
 * Quantas zonas de monstro cada lado tem. docs/01-regras-do-duelo.md secao 1
 *
 * Mora aqui, e nao em `packages/engine/src/constants.ts`, pelo mesmo motivo de `TRIBUTE_ONE_LEVEL`
 * logo abaixo: `packages/content` tambem precisa dela — para validar o mapa de terrenos de um
 * capitulo do Duelo Guiado (docs/20 secao 4.1) — e o `content` nao depende do `engine`. O motor a
 * reexporta, para que a entrada unica de constantes de regra continue valendo (docs/14 secao 3).
 */
export const MONSTER_ZONE_COUNT = 5

/**
 * Invocacao-Normal por Tributo. docs/01-regras-do-duelo.md secao 4
 *
 * Vive aqui, e nao em `packages/engine/src/constants.ts` como as demais constantes de regra,
 * porque `packages/content` tambem precisa dela — para checar a curva dos decks nossos — e o
 * `content` nao depende do `engine`. O motor a reexporta, para que a entrada unica de constantes
 * de regra continue valendo.
 *
 * Nao existe patamar de 3: o manual e explicito, e exigir tres para nivel 9/10 vem do manga.
 */
export const TRIBUTE_ONE_LEVEL = 5
export const TRIBUTE_TWO_LEVEL = 7

/** Quantos Tributos a Invocacao-Normal de um monstro deste nivel custa. docs/01 secao 4 */
export function tributesRequiredFor(level: number): number {
  if (level >= TRIBUTE_TWO_LEVEL) return 2
  if (level >= TRIBUTE_ONE_LEVEL) return 1
  return 0
}

/**
 * O caminho de entrada do monstro que nao entra pela Invocacao-Normal (`noNormalSummon`). docs/01-regras-do-duelo.md
 * secao 4.6, docs/02-catalogo-de-cartas.md secao 2, ADR 0053
 *
 * `TRIBUTE_CARD_IDS`: Invocacao-Especial da MAO, pela acao `PROCEDURE_SUMMON`, oferecendo como Tributo exatamente os
 * monstros de `cardIds`, do proprio campo — multiconjunto, como a receita de Fusao (Gate Guardian). Os nomes vivem no
 * dado: nenhum id de carta entra no motor (docs/99).
 *
 * `TRIBUTE_EQUIPPED_MONSTER`: Invocacao-Especial da MAO, pela acao `PROCEDURE_SUMMON`, oferecendo como Tributo 1 monstro
 * de `cardIds` do proprio campo, equipado com um Equipamento-monstro de `equipCardIds` que o mesmo jogador controla, cujo
 * contador de turnos esta EXATAMENTE em `ownTurn` (Larvae Moth e Great Moth, com o Pequeno Moth e o Casulo).
 *
 * `BY_CARD_EFFECT`: so a Invocacao-Especial feita pelo EFEITO de uma carta de `cardIds` (`SPECIAL_SUMMON`), que alcanca o
 * monstro sem a marca de Invocacao correta e a grava (Harpie Lady Sisters, pela Elegant Egotist). Nao tem acao propria.
 */
export const summonProcedureSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('TRIBUTE_CARD_IDS'), cardIds: z.array(z.string().min(1)).min(1) }),
  z.object({
    kind: z.literal('TRIBUTE_EQUIPPED_MONSTER'),
    cardIds: z.array(z.string().min(1)).min(1),
    equipCardIds: z.array(z.string().min(1)).min(1),
    ownTurn: z.number().int().positive(),
  }),
  z.object({ kind: z.literal('BY_CARD_EFFECT'), cardIds: z.array(z.string().min(1)).min(1) }),
])
export type SummonProcedure = z.infer<typeof summonProcedureSchema>
export type SummonProcedureKind = SummonProcedure['kind']

export const monsterCardSchema = baseCardSchema.extend({
  kind: z.literal('MONSTER'),
  /**
   * "Nao pode ser Invocado por Invocacao-Normal/Baixado": o reducer recusa a Invocacao-Normal, o Baixar e a extra
   * (`ILLEGAL_ACTION`), e a enumeracao nao os oferece. Anda sempre com `summonProcedure` — o loader recusa um sem o
   * outro. Ausente e `false`. docs/01-regras-do-duelo.md secoes 4 e 4.6, ADR 0053
   */
  noNormalSummon: z.boolean().optional(),
  /** O procedimento proprio do monstro com `noNormalSummon`. docs/01 secao 4.6, ADR 0053 */
  summonProcedure: summonProcedureSchema.optional(),
  /**
   * Monstro do EXTRA DECK que entra pelo procedimento proprio (`TRIBUTE_CARD_IDS`), e nao por Fusao: vive no Extra Deck,
   * como a Fusao, e nunca no deck principal nem na mao; a `PROCEDURE_SUMMON` o tira de la. Regra da casa (Gate Guardian,
   * decisao do Paulo de 18/10/2026). Ausente e `false`. docs/01-regras-do-duelo.md secoes 1 e 4.6, ADR 0053 secao 8
   */
  fromExtraDeck: z.boolean().optional(),
  /**
   * Monstro de FUSAO: vive no Extra Deck, e nunca no deck principal.
   * docs/01-regras-do-duelo.md secoes 1 e 7
   *
   * A marca e explicita, e nao derivada de `fusions.json`: dado derivado obriga quem le a
   * consultar outra tabela para responder "esta carta vai no deck principal?", que e a pergunta
   * mais frequente sobre uma carta.
   */
  fusion: z.boolean().default(false),
  /**
   * Monstro de RITUAL: fica no Deck principal, e so entra em campo por Invocacao-Ritual.
   * docs/01-regras-do-duelo.md secao 4.3
   *
   * Ao contrario do de Fusao, que a marca acima tira do deck principal, este ENTRA nele. E a
   * distincao que o manual faz, e a marca e explicita pelo mesmo motivo: dado derivado obrigaria
   * a consultar `rituals.json` para responder onde a carta vai.
   */
  ritual: z.boolean().default(false),
  level: z.number().int().min(1).max(12),
  attribute: z.enum(ATTRIBUTES),
  type: z.enum(MONSTER_TYPES),
  atk: z.number().int().min(0),
  def: z.number().int().min(0),
  effects: z.array(cardEffectSchema).default([]),
})
export type MonsterCard = z.infer<typeof monsterCardSchema>

/** O lado em que um Equipamento pode equipar. docs/02-catalogo-de-cartas.md secao 3.6 */
export const EQUIP_TARGET_SIDES = ['SELF'] as const
export type EquipTargetSide = (typeof EQUIP_TARGET_SIDES)[number]

export const spellCardSchema = baseCardSchema.extend({
  kind: z.literal('SPELL'),
  spellKind: z.enum(SPELL_KINDS),
  /** Obrigatorio quando spellKind === 'FIELD'. docs/01-regras-do-duelo.md secao 3.3 */
  fieldTerrain: z.enum(TERRAINS).optional(),
  /**
   * So para `EQUIP`: equipar em monstro de outro tipo e jogada ilegal, `INVALID_TARGET` (docs/02 secao 3.6).
   * Diferente de `EQUIPPED_TYPE_IS`, que permite equipar e apenas nao concede o bonus.
   */
  equipTargetType: z.enum(MONSTER_TYPES).optional(),
  /**
   * So para `EQUIP`: equipar em monstro de outro ATRIBUTO e ACAO ILEGAL (docs/02 secao 3.6).
   *
   * Irmao de `equipTargetType`, e nao substituto: a carta escolhe por qual eixo restringe, e
   * nenhum atributo e dedutivel de tipo nenhum. Declarados juntos, valem os dois.
   */
  equipTargetAttribute: z.enum(ATTRIBUTES).optional(),
  /**
   * So para `EQUIP`: equipar em monstro de outro LADO e jogada ilegal (docs/02 secao 3.6). `SELF` e o campo de quem
   * ativa o Equipamento (Ring of Magnetism, `leva_mrd/curadoria`). Lido so na ativacao.
   */
  equipTargetSide: z.enum(EQUIP_TARGET_SIDES).optional(),
  /**
   * Prazo proprio da carta em campo, contado em turnos do OPONENTE.
   * docs/01-regras-do-duelo.md secao 6.3
   *
   * NAO e duracao de efeito: quem sai de campo e a carta inteira, e nao um modificador. So faz
   * sentido em carta que FICA em campo, e o loader recusa a combinacao contraria.
   */
  lifespanInOpponentTurns: z.number().int().positive().optional(),
  effects: z.array(cardEffectSchema).default([]),
})
export type SpellCard = z.infer<typeof spellCardSchema>

export const trapCardSchema = baseCardSchema.extend({
  kind: z.literal('TRAP'),
  trapKind: z.enum(TRAP_KINDS).default('NORMAL'),
  /**
   * Prazo proprio da carta em campo, contado em turnos do OPONENTE.
   * docs/01-regras-do-duelo.md secao 6.3
   *
   * NAO e duracao de efeito: quem sai de campo e a carta inteira, e nao um modificador. So faz
   * sentido em carta que FICA em campo, e o loader recusa a combinacao contraria.
   */
  lifespanInOpponentTurns: z.number().int().positive().optional(),
  effects: z.array(cardEffectSchema).min(1),
})
export type TrapCard = z.infer<typeof trapCardSchema>

export const cardSchema = z.discriminatedUnion('kind', [monsterCardSchema, spellCardSchema, trapCardSchema])
export type Card = z.infer<typeof cardSchema>

export const fusionRecipeSchema = z.object({
  result: z.string().min(1),
  materials: z.array(z.string().min(1)).min(2),
})
export type FusionRecipe = z.infer<typeof fusionRecipeSchema>

/**
 * O vinculo entre a Magia de Ritual e o monstro que ela invoca.
 * docs/02-catalogo-de-cartas.md secao 4.1
 *
 * Mesma forma da receita de fusao, e pelo mesmo motivo: carta e dado, nunca codigo.
 */
export const ritualRecipeSchema = z.object({
  spell: z.string().min(1),
  monster: z.string().min(1),
})
export type RitualRecipe = z.infer<typeof ritualRecipeSchema>

/**
 * Chave canonica de uma receita de fusao: materiais ordenados.
 * A ordem informada pelo jogador nao importa. docs/02-catalogo-de-cartas.md secao 4
 */
export const fusionKey = (materials: readonly string[]): string => [...materials].sort().join('|')

/**
 * A carta permanece em campo depois de ativada? docs/01-regras-do-duelo.md secao 6
 *
 * Uma pergunta so, respondida num lugar so: Equipamento, Campo e as Continuas ficam; o resto vai
 * ao cemiterio ao resolver. Espalhar a lista seria criar lugares onde ela diverge.
 */
/**
 * A carta e um "Equipamento temporario": o efeito da ela um bonus de ATK/DEF que dura ate o fim
 * do turno (docs/01-regras-do-duelo.md secao 6). E o caso de Muralhas do Castelo e Reforcos —
 * Armadilhas cujo texto nao reage a nada especifico, so aumenta um numero "ate o fim do turno".
 *
 * A carta FICA em campo, marcando o monstro pela mesma `equippedTo` do Equipamento de verdade, e
 * so vai ao cemiterio quando o bonus expira (fim do turno) ou quando o monstro marcado sai de
 * campo antes disso — as duas pela MESMA regra do Equipamento, nao uma regra paralela.
 */
export const marksEquippedUntilEndOfTurn = (card: Card): boolean =>
  card.effects.some(
    (effect) =>
      effect.trigger === 'ON_ACTIVATE' &&
      effect.actions.some(
        (action) => (action.op === 'MODIFY_ATK' || action.op === 'MODIFY_DEF') && action.duration === 'END_OF_TURN',
      ),
  )

export const staysOnField = (card: Card): boolean =>
  (isSpell(card) && (card.spellKind === 'EQUIP' || card.spellKind === 'FIELD' || card.spellKind === 'CONTINUOUS')) ||
  (isTrap(card) && card.trapKind === 'CONTINUOUS') ||
  marksEquippedUntilEndOfTurn(card)

/**
 * Limites do efeito CONSULTADO de uma carta Continua. docs/01-regras-do-duelo.md secao 6.0
 *
 * Uma Continua e consultada, e nao disparada: ela nao tem contexto de gatilho. Condicao que fala
 * de um atacante ou de um recem-invocado nao significa nada ali, e alvo de escolha tambem nao —
 * ninguem escolhe durante um calculo. Ficam aqui, e nao no motor, porque quem as cobra e o
 * `packages/content`, que nao depende do motor.
 *
 * A excecao e `THIS_CARD_ATTACKS_ATTRIBUTE`: o unico contexto que ela le e o do calculo da batalha, que o motor passa
 * so ali. O loader a limita ao `MODIFY_ATK` em `THIS_CARD` de um monstro. docs/01 secao 6.0, item 7
 */
export const CONSULTABLE_CONDITION_OPS: readonly EffectConditionOp[] = [
  'ALWAYS',
  'SELF_LP_LTE',
  'SELF_LP_GTE',
  'OPPONENT_LP_LTE',
  'SELF_MONSTER_COUNT_LTE',
  'OPPONENT_MONSTER_COUNT_GTE',
  'TERRAIN_IS',
  'THIS_CARD_ATTACKS_ATTRIBUTE',
  // O que ela le (carta face-up em campo) e publico: o estado e a visao dao a mesma resposta (Pumpking). docs/02 secao 3.4
  'HAS_CARD_IDS_ON_ANY_FIELD',
]

export const CONSULTABLE_TARGETS: readonly EffectTarget[] = [
  'ALL_MONSTERS',
  'ALL_OPPONENT_MONSTERS',
  'ALL_MONSTERS_OF_TYPE',
  'ALL_MONSTERS_OF_ATTRIBUTE',
  // So o bonus do proprio monstro (`MODIFY_ATK`/`MODIFY_DEF`), e o loader o recusa noutra consulta. docs/01 secao 6.0
  'THIS_CARD',
]

/**
 * Alvos que ESCOLHEM um monstro EM CAMPO, que `PREVENT_TARGETING` alcanca.
 * docs/01-regras-do-duelo.md secao 6.0
 *
 * Conjunto FECHADO, e maior que so os `_CHOICE`: "ser escolhido como alvo" nao exige uma LISTA de
 * candidatos — um efeito que mira "o monstro que acabou de ser invocado" (`SUMMONED_MONSTER`, o
 * caso do Buraco Armadilha) ESCOLHE esse monstro como alvo tanto quanto um `_CHOICE` escolhe, so
 * que sem oferecer opcao nenhuma. Por isso `ATTACKING_MONSTER`, `DEFENDING_MONSTER` e
 * `SUMMONED_MONSTER` entram aqui: cada um resolve para UM monstro especifico, e e esse monstro que
 * a acao mira.
 *
 * `EQUIPPED_MONSTER` fica de FORA — e a excecao consciente. Ele nao e um novo ato de mirar: e a
 * REFERENCIA ao monstro que um Equipamento ja anexou, usada por uma acao (`MODIFY_ATK`/`MODIFY_DEF`
 * `WHILE_EQUIPPED`) que so LE o vinculo ja resolvido. O ato de mirar de verdade acontece quando o
 * Equipamento e ativado — e por isso a validacao de alvo do Equipamento (`actions.ts`), e nao esta
 * lista, e quem recusa equipar num monstro protegido.
 *
 * "Ser alcancado por efeito em massa" e outra coisa, e fica de fora dos dois jeitos: um `ALL_*`
 * nao escolhe ninguem, so afeta todo mundo que a descricao alcanca — e a carta que criou esta
 * lista (Lord of D.) so fala da primeira. `ANY_GRAVEYARD_MONSTER_CHOICE` tambem fica de fora,
 * porque aponta pro cemiterio, nao pro campo.
 */
export const PROTECTABLE_MONSTER_TARGETS: readonly EffectTarget[] = [
  'SELF_MONSTER_CHOICE',
  'OPPONENT_MONSTER_CHOICE',
  'ANY_MONSTER_CHOICE',
  'ANY_FACE_UP_MONSTER_CHOICE',
  'OPPONENT_FACE_UP_ATTACK_MONSTER_CHOICE',
  'OPPONENT_LOWEST_ATK_CHOICE',
  'ATTACKING_MONSTER',
  'DEFENDING_MONSTER',
  'SUMMONED_MONSTER',
  // Equipar o Casulo e escolher o Moth (Cocoon of Evolution, ADR 0053 secao 4).
  'SELF_FACE_UP_MONSTER_CHOICE',
]

/**
 * A carta e Continua — consultada enquanto estiver face-up em campo. docs/01-regras-do-duelo.md secao 6.0
 *
 * Magia e Armadilha so ficam em campo com `spellKind`/`trapKind` `CONTINUOUS` — e a excecao. Monstro
 * NAO precisa de marca nenhuma: face-up em campo ja e o estado natural dele, sem uma forma "monstro
 * continuo" contra "monstro que resolve e vai embora" (docs/01 secao 6.0 item 4).
 */
export const isContinuous = (card: Card): boolean =>
  isMonster(card) ||
  (isSpell(card) && card.spellKind === 'CONTINUOUS') ||
  (isTrap(card) && card.trapKind === 'CONTINUOUS')

export const isMonster = (card: Card): card is MonsterCard => card.kind === 'MONSTER'

/** Monstro de Fusao: pertence ao Extra Deck. docs/01-regras-do-duelo.md secao 7 */
export const isFusionMonster = (card: Card): boolean => isMonster(card) && card.fusion
/**
 * Monstro que pertence ao Extra Deck: o de Fusao e o do procedimento proprio que vem de la (`fromExtraDeck`). E a
 * pergunta da montagem do deck — "vai no Extra Deck?". docs/01-regras-do-duelo.md secoes 1 e 4.6
 */
export const isExtraDeckMonster = (card: Card): boolean =>
  isMonster(card) && (card.fusion || card.fromExtraDeck === true)
export const isSpell = (card: Card): card is SpellCard => card.kind === 'SPELL'
export const isTrap = (card: Card): card is TrapCard => card.kind === 'TRAP'
